// ============================================================
// Moteur PUR de la publication de planning — AUCUNE dépendance Angular.
// Testé sans TestBed : hr-publication.spec.ts.
// ============================================================
//
// Exigence explicite de la démo : « vous pouvez republier le planning UNIQUEMENT pour ceux qui
// sont concernés par un changement ». Republier à quinze personnes parce qu'on a déplacé un
// créneau, c'est le meilleur moyen de faire ignorer les notifications suivantes.
//
// PRINCIPE — on calcule une EMPREINTE par salarié à partir de ses créneaux de la semaine. À la
// republication, on compare à l'empreinte de la dernière publication : seuls ceux dont
// l'empreinte a changé sont notifiés. L'empreinte est stable (créneaux triés) et lisible, ce
// qui rend le diff explicable si quelqu'un conteste avoir été prévenu.

import { EffectiveSlot } from './hr-schedule-engine';

export interface PublicationDigest {
	[id_employee: string]: string;			// empreinte des créneaux de la personne
}

export interface PublicationDiff {
	added: number[];			// salariés qui n'avaient rien de publié et en ont maintenant
	changed: number[];		// salariés dont les créneaux ont bougé
	removed: number[];		// salariés qui avaient un planning et n'en ont plus
	unchanged: number[];
	// Qui doit recevoir une notification : ajoutés + modifiés + retirés (« tu ne travailles
	// plus mardi » est une information au moins aussi importante qu'un ajout).
	toNotify: number[];
}

/// Empreinte d'un salarié : ses créneaux non absents, triés, en une chaîne compacte.
/// « 2026-07-13:540-1020:30|2026-07-14:540-1020:0 »
export function employeeDigest(slots: EffectiveSlot[]): string {
	return slots
		.filter(s => !s.absent)
		.map(s => s.dat + ':' + s.start_min + '-' + s.end_min + ':' + (s.break_min ?? 0))
		.sort()
		.join('|');
}

/// Empreinte complète d'une semaine, salarié par salarié.
/// Un salarié sans aucun créneau n'apparaît PAS : c'est ce qui permet de détecter un retrait.
export function buildPublicationDigest(slots: EffectiveSlot[]): PublicationDigest {
	const byEmployee = new Map<number, EffectiveSlot[]>();
	for (const s of slots) {
		if (!byEmployee.has(s.id_employee)) byEmployee.set(s.id_employee, []);
		byEmployee.get(s.id_employee)!.push(s);
	}
	const out: PublicationDigest = {};
	for (const [id, list] of byEmployee) {
		const digest = employeeDigest(list);
		if (digest) out['' + id] = digest;			// vide (que des absences) = pas de planning
	}
	return out;
}

/// Compare l'empreinte courante à celle de la dernière publication.
/// `previous` vide (jamais publié) ⇒ tout le monde est « ajouté ».
export function publicationDiff(current: PublicationDigest, previous: PublicationDigest | null): PublicationDiff {
	const prev = previous ?? {};
	const added: number[] = [];
	const changed: number[] = [];
	const removed: number[] = [];
	const unchanged: number[] = [];

	for (const key of Object.keys(current)) {
		const id = +key;
		if (!(key in prev)) added.push(id);
		else if (prev[key] !== current[key]) changed.push(id);
		else unchanged.push(id);
	}
	for (const key of Object.keys(prev)) {
		if (!(key in current)) removed.push(+key);
	}

	const num = (a: number, b: number) => a - b;
	return {
		added: added.sort(num),
		changed: changed.sort(num),
		removed: removed.sort(num),
		unchanged: unchanged.sort(num),
		toNotify: [...added, ...changed, ...removed].sort(num),
	};
}

/// Phrase d'aperçu affichée AVANT de publier — on ne notifie personne à l'aveugle.
export function publicationSummary(diff: PublicationDiff, nameOf: (id: number) => string): string {
	if (!diff.toNotify.length) {
		return 'Aucun changement depuis la dernière publication : personne ne sera notifié.';
	}
	const parts: string[] = [];
	if (diff.added.length) parts.push(diff.added.length + ' nouveau(x) planning(s)');
	if (diff.changed.length) parts.push(diff.changed.length + ' modifié(s)');
	if (diff.removed.length) parts.push(diff.removed.length + ' retiré(s)');

	const names = diff.toNotify.slice(0, 5).map(nameOf).join(', ');
	const more = diff.toNotify.length > 5 ? ' et ' + (diff.toNotify.length - 5) + ' autre(s)' : '';
	return diff.toNotify.length + ' salarié(s) seront notifiés — ' + parts.join(', ') + '.\n' + names + more;
}
